import { useState } from 'react';
import { useAuthStore } from '@shared/store/authStore';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface Membership {
  id: number;
  name: string;
  type: 'card' | 'abonement';
  status: 'active' | 'frozen' | 'expired';
  startDate: string;
  endDate: string;
  remainingVisits?: number;
  totalVisits?: number;
  services: string[];
  price: number;
}

interface AvailableCard {
  id: number;
  name: string;
  description: string;
  price: number;
  duration: number;
  services: string[];
  popular: boolean;
}

// TODO: Load from Universe API when methods are available
const userMemberships: Membership[] = [];
const availableCards: AvailableCard[] = [];

const statusConfig = {
  active: { label: 'Активен', color: 'bg-green-100 text-green-800', icon: '✓' },
  frozen: { label: 'Заморожен', color: 'bg-blue-100 text-blue-800', icon: '❄️' },
  expired: { label: 'Истек', color: 'bg-gray-100 text-gray-800', icon: '⏰' }
};

export function MembershipPage() {
  const { user, apiClient } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'my' | 'buy'>('my');
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleBuyCard = async (cardId: number) => {
    if (!user) return;

    try {
      setIsLoading(true);
      // TODO: Implement with Universe API
      alert('Функция покупки абонемента будет доступна после интеграции с Universe API');
      setActiveTab('my');
    } catch (error) {
      console.error('Error buying card:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFreezeCard = (cardId: number) => {
    // Implementation for freezing card
  };

  const handleUnfreezeCard = (cardId: number) => {
    // Implementation for unfreezing card
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Абонементы и карты</h1>
        <p className="text-gray-600">Управляйте своими абонементами</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('my')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'my'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Мои абонементы
        </button>
        <button
          onClick={() => setActiveTab('buy')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'buy'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Купить новый
        </button>
      </div>

      {/* My Memberships Tab */}
      {activeTab === 'my' && (
        <div className="space-y-4">
          {userMemberships.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет активных абонементов</h3>
              <p className="text-gray-600 mb-6">Приобретите абонемент для доступа к услугам клуба</p>
              <button
                onClick={() => setActiveTab('buy')}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                Выбрать абонемент
              </button>
            </div>
          ) : (
            userMemberships.map((membership) => {
              const status = statusConfig[membership.status];
              const progress = membership.remainingVisits && membership.totalVisits 
                ? ((membership.totalVisits - membership.remainingVisits) / membership.totalVisits) * 100
                : 0;

              return (
                <div key={membership.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{membership.name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${status.color}`}>
                          {status.icon} {status.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Действует с {format(new Date(membership.startDate), 'dd MMMM yyyy', { locale: ru })} по {format(new Date(membership.endDate), 'dd MMMM yyyy', { locale: ru })}</p>
                        {membership.remainingVisits !== undefined && (
                          <p>Осталось посещений: {membership.remainingVisits} из {membership.totalVisits}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">{membership.price} ₽</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {membership.remainingVisits !== undefined && membership.totalVisits && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Использовано</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Services */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Включенные услуги:</p>
                    <div className="flex flex-wrap gap-2">
                      {membership.services.map((service, index) => (
                        <span key={index} className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3">
                    {membership.status === 'active' && (
                      <button
                        onClick={() => handleFreezeCard(membership.id)}
                        className="flex-1 py-2 px-4 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
                      >
                        ❄️ Заморозить
                      </button>
                    )}
                    {membership.status === 'frozen' && (
                      <button
                        onClick={() => handleUnfreezeCard(membership.id)}
                        className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        🔥 Разморозить
                      </button>
                    )}
                    <button className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                      Подробнее
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Buy New Tab */}
      {activeTab === 'buy' && (
        <div className="space-y-4">
          {availableCards.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет доступных абонементов</h3>
              <p className="text-gray-600">Функция будет доступна после интеграции с Universe API</p>
            </div>
          )}

          {availableCards.map((card) => (
            <div
              key={card.id}
              className={`bg-white rounded-2xl p-6 shadow-sm border transition-all ${
                card.popular
                  ? 'border-emerald-500 ring-2 ring-emerald-100'
                  : 'border-gray-100'
              }`}
            >
              {card.popular && (
                <div className="flex justify-center mb-4">
                  <span className="px-3 py-1 bg-emerald-600 text-white text-sm rounded-full">
                    ⭐ Популярный выбор
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{card.name}</h3>
                <p className="text-gray-600 mb-4">{card.description}</p>
                <div className="flex items-baseline justify-center space-x-1">
                  <span className="text-3xl font-bold text-emerald-600">{card.price}</span>
                  <span className="text-gray-600">₽</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">На {card.duration} дней</p>
              </div>

              {/* Services */}
              <div className="mb-6">
                <p className="font-medium text-gray-900 mb-3">Что включено:</p>
                <div className="space-y-2">
                  {card.services.map((service, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Purchase Button */}
              <button
                onClick={() => handleBuyCard(card.id)}
                disabled={isLoading}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center ${
                  card.popular
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Оформляем...
                  </>
                ) : (
                  'Купить абонемент'
                )}
              </button>
            </div>
          ))}

          {/* Info Block */}
          <div className="bg-blue-50 rounded-2xl p-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Полезная информация</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Абонементы можно заморозить на срок до 30 дней</li>
              <li>• При покупке нового абонемента действует скидка 10%</li>
              <li>• Возможна рассрочка на все абонементы от 6 месяцев</li>
              <li>• Семейные абонементы со скидкой до 25%</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}